import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Stack,
  LinearProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';
import { useColorScheme } from '../../context/ColorSchemeContext';
import { BRAND } from '../../theme/brandTheme';
import ArticleIcon from '@mui/icons-material/Article';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaletteIcon from '@mui/icons-material/Palette';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InsightsIcon from '@mui/icons-material/Insights';
import GavelIcon from '@mui/icons-material/Gavel';
import { COLOR_SCHEMES } from '../../utils/color-schemas';

// LocalStorage keys
const ACCORDION_STATE_KEY = 'stats_accordion_expanded';

/**
 * Dashboard istatistik kartları ve grafikler — embedded accordion veya standalone sayfa
 */
const StatsCards = ({ stats, user, standalone = false, hideBrandChart = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colorScheme = useColorScheme();
  
  // Renk şeması seçici için state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  // Accordion state — only used on dashboard embed
  const [expanded, setExpanded] = useState(() => {
    if (standalone) return true;
    const savedState = localStorage.getItem(ACCORDION_STATE_KEY);
    return savedState === 'true';
  });

  useEffect(() => {
    if (standalone) return;
    localStorage.setItem(ACCORDION_STATE_KEY, expanded.toString());
  }, [expanded, standalone]);
  
  // Stats verisi henüz yüklenmediyse
  if (!stats) {
    return null;
  }

  // Temel istatistikler
  const { totalReports, activeTests, significantResults, metricsStats, charts } = stats;

  const winningTests =
    metricsStats?.winningTestCount ??
    charts?.resultStatusDistribution?.find((item) => item.id === 'Kazandı')?.value ??
    0;
  const losingTests = metricsStats?.losingTestCount ?? 0;
  const inconclusiveTests = metricsStats?.inconclusiveTestCount ?? 0;
  const significantTests =
    metricsStats?.significantTestCount ?? significantResults ?? 0;
  const testsWithResult =
    metricsStats?.testsWithResultCount ?? metricsStats?.reportsWithResultCount ?? 0;
  
  // Renk şemaları
  const getColors = (count, type = 'primary') => {
    return colorScheme ? colorScheme.getColors(count, type) : [];
  };
  
  // Chart renkleri - color scheme'den alınacak
  const statusColors = {
    'Canlı': colorScheme ? colorScheme.getColor(0, 'primary') : '#2E74B5',
    'Taslak': colorScheme ? colorScheme.getColor(1, 'primary') : '#4472C4',
    'Durduruldu': colorScheme ? colorScheme.getColor(2, 'primary') : '#FFC000'
  };
  
  const performanceColors = {
    'Pozitif': colorScheme ? colorScheme.getColor(0, 'primary') : '#70AD47',
    'Negatif': colorScheme ? colorScheme.getColor(1, 'primary') : '#C00000',
    'Nötr': colorScheme ? colorScheme.getColor(2, 'primary') : '#7F7F7F'
  };
  
  const resultStatusColors = {
    'Kazandı': colorScheme ? colorScheme.getColor(0, 'primary') : '#70AD47',
    'Kaybetti': colorScheme ? colorScheme.getColor(1, 'primary') : '#C00000',
    'Etkisiz': colorScheme ? colorScheme.getColor(2, 'primary') : '#7F7F7F'
  };

  const decisionColors = {
    Uygula: '#27AE60',
    Reddet: '#E74C3C',
    'Tekrar Test': '#E67E22',
    Beklemede: '#95A5A6',
  };

  const activePercentage = totalReports > 0 ? Math.round((activeTests / totalReports) * 100) : 0;
  const winningPercentage =
    testsWithResult > 0 ? Math.round((winningTests / testsWithResult) * 100) : 0;
  const losingPercentage =
    testsWithResult > 0 ? Math.round((losingTests / testsWithResult) * 100) : 0;
  const significantPercentage =
    totalReports > 0 ? Math.round((significantTests / totalReports) * 100) : 0;
  
  const brandColors = colorScheme ? getColors(1, 'primary') : [BRAND.purple, '#2E74B5', BRAND.green, '#FFC000', '#C00000', '#4472C4'];

  const monthlyChartColors = colorScheme ? getColors(3, 'accent') : [BRAND.purple, '#2E74B5', '#4472C4'];
  
  // Renk şeması değiştirme işleyicileri
  const handleColorMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleColorMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleColorSchemeChange = (schemeId) => {
    if (colorScheme) {
      colorScheme.changeScheme(schemeId);
    }
    handleColorMenuClose();
  };
  
  // Accordion durum değişikliği
  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };
  
  // Geçerli renk şeması
  const currentScheme = colorScheme ? colorScheme.scheme : 'default';
  const availableSchemes = colorScheme ? colorScheme.schemes : [];

  // Admin kontrolü
  const isAdmin = user?.role === 'admin';

  const statsContent = (
    <>
        {/* Renk Şeması Seçici */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
              Renk Şeması:
            </Typography>
            <Tooltip title="Renk şemasını değiştir">
              <IconButton 
                size="small" 
                onClick={handleColorMenuOpen}
                sx={{ 
                  border: '1px solid rgba(0,0,0,0.1)', 
                  borderRadius: '4px',
                  ml: 1,
                  p: '4px 8px',
                  fontSize: '0.8rem',
                  textTransform: 'none'
                }}
              >
                <Typography variant="body2" component="span" sx={{ mr: 1 }}>
                  {COLOR_SCHEMES[currentScheme]?.name || 'Default'}
                </Typography>
                <PaletteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleColorMenuClose}
              sx={{ maxHeight: '300px' }}
            >
              {availableSchemes.map((scheme) => (
                <MenuItem 
                  key={scheme.id} 
                  onClick={() => handleColorSchemeChange(scheme.id)}
                  selected={currentScheme === scheme.id}
                  sx={{ 
                    minWidth: '120px',
                    backgroundColor: currentScheme === scheme.id ? 'rgba(0,0,0,0.04)' : 'transparent'
                  }}
                >
                  {scheme.name}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>

        {/* Temel Stat Kartları */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: colorScheme?.getColor(0, 'primary') || BRAND.purple }}>
                      {totalReports}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Toplam Rapor
                    </Typography>
                  </Box>
                  <ArticleIcon 
                    sx={{ 
                      fontSize: 40, 
                      color: colorScheme?.getColor(0, 'secondary') || '#6588DF',
                    }} 
                  />
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  {user?.brands?.length > 0
                    ? "Erişim yetkisi olan markalara ait raporlar"
                    : "Tüm raporların toplam sayısı"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: colorScheme?.getColor(1, 'primary') || BRAND.green }}>
                      {winningTests}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Kazanan Test
                    </Typography>
                  </Box>
                  <CheckCircleIcon 
                    sx={{ 
                      fontSize: 40, 
                      color: colorScheme?.getColor(1, 'secondary') || '#5ADC87',
                    }} 
                  />
                </Stack>
                <LinearProgress 
                  variant="determinate" 
                  value={winningPercentage} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 1,
                    mb: 1, 
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: colorScheme?.getColor(1, 'primary') || BRAND.green
                    }
                  }} 
                />
                <Typography variant="body2" color="text.secondary">
                  Sonuçlanmış testler içinde kazanan oranı ({winningPercentage}% · {winningTests}/{testsWithResult})
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: colorScheme?.getColor(2, 'primary') || '#E57702' }}>
                      {activeTests}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Aktif Test
                    </Typography>
                  </Box>
                  <PlayCircleFilledIcon 
                    sx={{ 
                      fontSize: 40, 
                      color: colorScheme?.getColor(2, 'secondary') || '#F7BE64',
                    }} 
                  />
                </Stack>
                <LinearProgress 
                  variant="determinate" 
                  value={activePercentage} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 1,
                    mb: 1, 
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: colorScheme?.getColor(2, 'primary') || '#E57702'
                    }
                  }} 
                />
                <Typography variant="body2" color="text.secondary">
                  Şu anda 'Canlı' durumda olan testler ({activePercentage}%)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Extended outcome & significance cards — statistics page */}
        {standalone && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: '#C00000' }}>
                        {losingTests}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Kaybeden Test
                      </Typography>
                    </Box>
                    <TrendingDownIcon sx={{ fontSize: 40, color: '#E57373' }} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Sonuçlanan testlerin %{losingPercentage}&apos;i ({losingTests}/{testsWithResult || '—'})
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: '#7F7F7F' }}>
                        {inconclusiveTests}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Etkisiz Test
                      </Typography>
                    </Box>
                    <HelpOutlineIcon sx={{ fontSize: 40, color: '#B0BEC5' }} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    İstatistiksel fark görülmeyen testler
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: '#2E74B5' }}>
                        {significantTests}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Anlamlı Sonuç
                      </Typography>
                    </Box>
                    <InsightsIcon sx={{ fontSize: 40, color: '#6588DF' }} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    En az bir anlamlı metriği olan testler (%{significantPercentage} portföy)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Chart Kartları */}
        {charts && (
          <Grid container spacing={3}>
            {/* Durum Dağılımı - Pie Chart */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Rapor Durumları
                  </Typography>
                  <Box sx={{ height: 200 }}>
                    <ResponsivePie
                      data={charts.statusDistribution}
                      margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                      innerRadius={0.5}
                      padAngle={0.7}
                      cornerRadius={3}
                      activeOuterRadiusOffset={8}
                      colors={({ id }) => statusColors[id] || '#999'}
                      borderWidth={1}
                      borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                      enableArcLinkLabels={false}
                      arcLabelsSkipAngle={10}
                      arcLabelsTextColor="#fff"
                      legends={[]}
                      tooltip={({ datum }) => (
                        <div style={{ 
                          background: 'white', 
                          padding: '5px 9px', 
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <strong>{datum.label}:</strong> {datum.value}
                        </div>
                      )}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Performans Dağılımı - Pie Chart */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Performans Sonuçları
                  </Typography>
                  <Box sx={{ height: 200 }}>
                    <ResponsivePie
                      data={charts.performanceDistribution}
                      margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                      innerRadius={0.5}
                      padAngle={0.7}
                      cornerRadius={3}
                      activeOuterRadiusOffset={8}
                      colors={({ id }) => performanceColors[id] || '#999'}
                      borderWidth={1}
                      borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                      enableArcLinkLabels={false}
                      arcLabelsSkipAngle={10}
                      arcLabelsTextColor="#fff"
                      legends={[]}
                      tooltip={({ datum }) => (
                        <div style={{ 
                          background: 'white', 
                          padding: '5px 9px', 
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <strong>{datum.label}:</strong> {datum.value}
                        </div>
                      )}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Sonuç Durumu Dağılımı - Pie Chart (YENİ) */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Test Sonuçları
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Toplam {testsWithResult} test sonucu
                    {metricsStats?.totalMetricCount
                      ? ` (${metricsStats.totalMetricCount} metrik)`
                      : ''}
                  </Typography>
                  <Box sx={{ height: 200 }}>
                    <ResponsivePie
                      data={charts.resultStatusDistribution || [
                        { id: 'Kazandı', label: 'Kazandı', value: 0 },
                        { id: 'Kaybetti', label: 'Kaybetti', value: 0 },
                        { id: 'Etkisiz', label: 'Etkisiz', value: 0 }
                      ]}
                      margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                      innerRadius={0.5}
                      padAngle={0.7}
                      cornerRadius={3}
                      activeOuterRadiusOffset={8}
                      colors={({ id }) => resultStatusColors[id] || '#999'}
                      borderWidth={1}
                      borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                      enableArcLinkLabels={false}
                      arcLabelsSkipAngle={10}
                      arcLabelsTextColor="#fff"
                      legends={[]}
                      tooltip={({ datum }) => (
                        <div style={{ 
                          background: 'white', 
                          padding: '5px 9px', 
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <strong>{datum.label}:</strong> {datum.value}
                        </div>
                      )}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Post-test decision distribution */}
            {charts.decisionDistribution && (
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      <GavelIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                      Karar Dağılımı
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Uygula / Reddet / Tekrar test / Beklemede
                    </Typography>
                    <Box sx={{ height: 200 }}>
                      <ResponsivePie
                        data={charts.decisionDistribution}
                        margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                        innerRadius={0.5}
                        padAngle={0.7}
                        cornerRadius={3}
                        activeOuterRadiusOffset={8}
                        colors={({ id }) => decisionColors[id] || '#999'}
                        borderWidth={1}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                        enableArcLinkLabels={false}
                        arcLabelsSkipAngle={10}
                        arcLabelsTextColor="#fff"
                        legends={[]}
                        tooltip={({ datum }) => (
                          <div style={{
                            background: 'white',
                            padding: '5px 9px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}>
                            <strong>{datum.label}:</strong> {datum.value} test
                          </div>
                        )}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
            
            {/* Aylık Rapor Sayıları - Bar Chart */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Son 6 Ayda Oluşturulan Raporlar
                  </Typography>
                  <Box sx={{ height: 200 }}>
                    <ResponsiveBar
                      data={charts.monthlyReports}
                      keys={['count']}
                      indexBy="month"
                      margin={{ top: 10, right: 10, bottom: 30, left: 40 }}
                      padding={0.3}
                      valueScale={{ type: 'linear' }}
                      indexScale={{ type: 'band', round: true }}
                      colors={monthlyChartColors}
                      borderWidth={1}
                      borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: isMobile ? -45 : 0,
                        legendPosition: 'middle',
                        legendOffset: 32
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legendPosition: 'middle',
                        legendOffset: -40
                      }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                      labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                      tooltip={({ data, value }) => (
                        <div style={{ 
                          background: 'white', 
                          padding: '5px 9px', 
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <strong>{data.month}:</strong> {value} rapor
                        </div>
                      )}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Marka dağılımı — hide when a single brand tab is selected */}
            {isAdmin && !hideBrandChart && (
              <Grid item xs={12}>
                <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Marka Dağılımı
                    </Typography>
                    <Box sx={{ height: 250 }}>
                      <ResponsiveBar
                        data={charts.brandDistribution}
                        keys={['value']}
                        indexBy="label"
                        margin={{ top: 10, right: 10, bottom: 40, left: 60 }}
                        padding={0.3}
                        valueScale={{ type: 'linear' }}
                        indexScale={{ type: 'band', round: true }}
                        colors={brandColors}
                        borderWidth={1}
                        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                        axisBottom={{
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: 0,
                          legend: 'Rapor Sayısı',
                          legendPosition: 'middle',
                          legendOffset: 32
                        }}
                        axisLeft={{
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: 0
                        }}
                        labelSkipWidth={12}
                        labelSkipHeight={12}
                        labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                        tooltip={({ data, value }) => (
                          <div style={{ 
                            background: 'white', 
                            padding: '5px 9px', 
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            <strong>{data.label}:</strong> {value} rapor
                          </div>
                        )}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
    </>
  );

  if (standalone) {
    return <Box>{statsContent}</Box>;
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={handleAccordionChange}
      sx={{
        mb: 4,
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: BRAND.purple }} />}
        sx={{
          borderBottom: expanded ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
          '&.Mui-expanded': { minHeight: '48px' },
        }}
      >
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: BRAND.purple }}>
          İstatistikler ve Grafikler
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 3 }}>{statsContent}</AccordionDetails>
    </Accordion>
  );
};

export default StatsCards; 